<script setup lang="ts">
import { connectPagination } from 'instantsearch.js/es/connectors';
import {
  PaginationConnectorParams,
  PaginationWidgetDescription,
} from 'instantsearch.js/es/connectors/pagination/connectPagination';
import { computed } from 'vue';
// @ts-expect-error (function lacks types; this should be converted in the near future)
import { createSuitMixin } from 'vue-instantsearch/vue3/es';

import { useWidget } from '@/composables/instant-search/useWidget';
import { scrollToTarget } from '@/utils/scrollTo';

defineOptions({
  mixins: [createSuitMixin({ name: 'Pagination' })],
});

const props = defineProps({
  padding: {
    required: false,
    type: Number,
    default: 2,
    validator(value: number) {
      return value > 0;
    },
  },
  totalPages: {
    required: false,
    type: Number,
    default: undefined,
    validator(value: number) {
      return value > 0;
    },
  },
  showFirst: { required: false, type: Boolean, default: true },
  showLast: { required: false, type: Boolean, default: true },
  showNext: { required: false, type: Boolean, default: true },
  showPrevious: { required: false, type: Boolean, default: true },
});

const emit = defineEmits(['page-change']);

const widgetParams = computed<PaginationConnectorParams>(() => ({
  padding: props.padding,
  totalPages: props.totalPages,
}));

const { state } = useWidget<PaginationWidgetDescription, PaginationConnectorParams>(
  { connector: connectPagination },
  widgetParams,
);

const createURL = (page: number) => state.value?.createURL(page);
const refine = (page: number) => {
  scrollToTarget('.ais-Hits');
  const p = Math.min(Math.max(page, 0), (state.value?.nbPages ?? 1) - 1);
  state.value?.refine(p);
  emit('page-change', p);
};
</script>

<template>
  <div v-if="state" :class="suit()" data-test="pagination">
    <slot
      :refine="refine"
      :createURL="createURL"
      :current-refinement="state.currentRefinement"
      :nb-hits="state.nbHits"
      :nb-pages="state.nbPages"
      :pages="state.pages"
      :is-first-page="state.isFirstPage"
      :is-last-page="state.isLastPage"
    >
      <ul :class="suit('list')" v-show="state.nbPages > 1">
        <li
          :class="{
            [suit('item')]: true,
            [suit('item', 'firstPage')]: true,
            [suit('item', 'disabled')]: state.isFirstPage,
          }"
          v-if="showFirst"
        >
          <slot
            name="first"
            :createURL="() => createURL(0)"
            :is-first-page="state.isFirstPage"
            :refine="() => refine(0)"
          >
            <template v-if="!state.isFirstPage">
              <a
                :class="suit('link')"
                aria-label="First"
                :href="createURL(0)"
                @click.prevent="refine(0)"
                >‹‹</a
              >
            </template>
            <template v-else>
              <span :class="suit('link')" aria-label="First">‹‹</span>
            </template>
          </slot>
        </li>
        <li
          :class="{
            [suit('item')]: true,
            [suit('item', 'previousPage')]: true,
            [suit('item', 'disabled')]: state.isFirstPage,
          }"
          v-if="showPrevious"
          v-show="!state.isFirstPage"
        >
          <slot
            name="previous"
            :createURL="() => createURL(state.currentRefinement - 1)"
            :is-first-page="state.isFirstPage"
            :refine="() => refine(state.currentRefinement - 1)"
          >
            <template v-if="!state.isFirstPage">
              <a
                :class="suit('link')"
                aria-label="Previous"
                :href="createURL(state.currentRefinement - 1)"
                @click.prevent="refine(state.currentRefinement - 1)"
              >
                <img src="@/assets/left_arrow_long.svg" alt="left arrow" />
                Prev
              </a>
            </template>
            <template v-else>
              <span :class="suit('link')" aria-label="Previous" data-test="previous">
                <img src="@/assets/left_arrow_long.svg" alt="left arrow" />
                Prev
              </span>
            </template>
          </slot>
        </li>

        <li
          :class="{
            [suit('item')]: true,
            [suit('item', 'selected')]: state.currentRefinement === page,
          }"
          v-for="page in state.pages"
          :key="page"
        >
          <slot
            name="item"
            :page="page"
            :createURL="() => createURL(page)"
            :is-first-page="state.isFirstPage"
            :is-last-page="state.isLastPage"
            :refine="() => refine(page)"
          >
            <a :class="suit('link')" :href="createURL(page)" @click.prevent="refine(page)">{{
              page + 1
            }}</a>
          </slot>
        </li>

        <li
          :class="{
            [suit('item')]: true,
            [suit('item', 'nextPage')]: true,
          }"
          v-if="showNext"
          v-show="!state.isLastPage"
        >
          <slot
            name="next"
            :createURL="() => createURL(state.currentRefinement + 1)"
            :is-last-page="state.isLastPage"
            :refine="() => refine(state.currentRefinement + 1)"
          >
            <template v-if="!state.isLastPage">
              <a
                :class="suit('link')"
                aria-label="Next"
                :href="createURL(state.currentRefinement + 1)"
                @click.prevent="refine(state.currentRefinement + 1)"
                data-test="next"
              >
                Next
                <img src="@/assets/left_arrow_long.svg" alt="right arrow" class="flipped" />
              </a>
            </template>
            <template v-else>
              <span :class="suit('link')" aria-label="Next">
                Next
                <img src="@/assets/left_arrow_long.svg" alt="right arrow" class="flipped" />
              </span>
            </template>
          </slot>
        </li>
        <li
          :class="{
            [suit('item')]: true,
            [suit('item', 'lastPage')]: true,
            [suit('item', 'disabled')]: state.isLastPage,
          }"
          v-if="showLast"
        >
          <slot
            name="last"
            :createURL="() => createURL(state.nbPages - 1)"
            :is-last-page="state.isLastPage"
            :refine="() => refine(state.nbPages - 1)"
          >
            <template v-if="!state.isLastPage">
              <a
                :class="suit('link')"
                aria-label="Last"
                :href="createURL(state.nbPages - 1)"
                @click.prevent="refine(state.nbPages - 1)"
                >››</a
              >
            </template>
            <template v-else>
              <span :class="suit('link')" aria-label="Last">››</span>
            </template>
          </slot>
        </li>
      </ul>
    </slot>
  </div>
</template>
